import { useEffect, useState } from "react";
import { api, ApiError, type CurrentPeriodResponse, type MeResponse } from "../api/client";
import { InfoTooltip } from "../components/Tooltip";
import { ProgressBar } from "../components/ProgressBar";
import { formatBytes, formatCents, formatCount, safeNumber } from "../lib/format";

export function UsagePage() {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [period, setPeriod] = useState<CurrentPeriodResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Always refetch on mount so navigating from Dashboard -> Usage does
  // not show stale numbers; the data is cheap to recompute on the
  // backend and customers expect "Usage" to be the authoritative view.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [m, p] = await Promise.all([api.me(), api.billingCurrentPeriod()]);
        if (cancelled) return;
        setMe(m);
        setPeriod(p);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) {
          if (err.code === "billing_not_configured") {
            // The /me endpoint still works; only the rich period data
            // comes from /billing/current-period. Degrade gracefully.
            try {
              const m = await api.me();
              if (!cancelled) setMe(m);
            } catch {
              // ignore; the outer error path renders below
            }
            setError(
              "Detailed billing breakdown is unavailable on this deployment. Usage counters below are accurate."
            );
          } else {
            setError(err.message);
          }
        } else {
          setError("Could not load usage.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error && !me && !period) {
    return (
      <div className="panel">
        <h2 className="section-title">Usage</h2>
        <div className="error">{error}</div>
      </div>
    );
  }
  if (!me) return <p className="muted">Loading…</p>;

  const customer = me.customer;
  const rawUsage = period?.usage ?? me.current_period;
  const liveUsage = {
    signatures_used: safeNumber(rawUsage?.signatures_used),
    verifications_used: safeNumber(rawUsage?.verifications_used),
    library_bytes_used: safeNumber(rawUsage?.library_bytes_used)
  };

  return (
    <>
      <div className="panel">
        <h2 className="section-title">
          This period
          <InfoTooltip content="Period boundaries align with UTC calendar months. Counters reset at 00:00:00 UTC on the first of each month." />
        </h2>
        <div className="stats-grid">
          <div className="stat">
            <div className="stat-label">
              Signatures
              <InfoTooltip content="Each cryptographic signature on a single agent action. A typical agent session uses 3-20 signatures." />
            </div>
            <div className="stat-value">{formatCount(liveUsage.signatures_used)}</div>
            <ProgressBar
              used={liveUsage.signatures_used}
              cap={customer.signature_quota_monthly}
              label="quota"
            />
          </div>
          <div className="stat">
            <div className="stat-label">
              Receipt library
              <InfoTooltip content="Sequesign-stored copies of your assembled receipts (Phase 3.5)." />
            </div>
            <div className="stat-value">{formatBytes(liveUsage.library_bytes_used)}</div>
            <ProgressBar
              used={liveUsage.library_bytes_used}
              cap={customer.receipt_library_quota_bytes}
              label="included"
              formatValue={formatBytes}
            />
          </div>
          <div className="stat">
            <div className="stat-label">
              Verifications
              <InfoTooltip content="Verifications are always free. Counted so you can see the activity, never billed." />
            </div>
            <div className="stat-value">{formatCount(liveUsage.verifications_used)}</div>
            <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
              always free
            </div>
          </div>
        </div>
        {period ? (
          <dl style={{ marginTop: 16 }}>
            <div className="row">
              <dt>Period start</dt>
              <dd>{period.period_start ?? "(no usage yet)"}</dd>
            </div>
            <div className="row">
              <dt>Period end</dt>
              <dd>{period.period_end ?? ""}</dd>
            </div>
          </dl>
        ) : null}
      </div>

      <div className="panel">
        <h2 className="section-title">
          Estimated charges
          <InfoTooltip content="Estimate based on current usage and your tier rates. The actual invoice is generated by Stripe at period close and may include proration, taxes, or minimum-charge handling." />
        </h2>
        {!period ? (
          <p className="muted">Charge estimates appear once billing is configured.</p>
        ) : period.estimated_charges.line_items.length === 0 ? (
          <p className="muted">
            {period.tier === "free"
              ? "Free tier doesn't accrue charges."
              : "No charges yet this period. Usage will accrue here as your agents sign actions."}
          </p>
        ) : (
          <dl>
            {period.estimated_charges.line_items.map((item, i) => (
              <div className="row" key={i}>
                <dt>{item.description}</dt>
                <dd>
                  {formatCount(item.quantity)} × {formatCents(item.unit_amount_cents)} ={" "}
                  {formatCents(item.amount_cents)}
                </dd>
              </div>
            ))}
            <div className="row">
              <dt>
                <strong>Total (estimated)</strong>
              </dt>
              <dd>
                <strong>{formatCents(period.estimated_charges.total_cents)}</strong>
              </dd>
            </div>
          </dl>
        )}
      </div>

      <div className="panel">
        <h2 className="section-title">Previous periods</h2>
        <p className="muted">
          Period-by-period history will appear here as months close. Today this shows the current
          period only; the historical roll-up lands with the automated period-close cron.
        </p>
      </div>
      {error ? <div className="error">{error}</div> : null}
    </>
  );
}
